import { Editor } from '@monaco-editor/react';
import { forwardRef, memo, useImperativeHandle, useRef } from 'react';

import { Option } from '@/lib/v2/components';
import { DropdownItemProps } from '@/lib/v2/components/Dropdown';

import './CustomEditor.tailwind.css';

export type OptionVariable = Option & { key: string };

export type CustomEditorProps = {
  name?: string;
  value?: string;
  variablesOptions: DropdownItemProps[];
  onValidate?: (hasErrors: boolean) => void;
};

type VariableEditor = { key: string; id: string; start: number; end: number; line: number };

const defaultLanguage = 'jsonWithVars';

export type CustomEditorRef = {
  getValue: () => string;
  insertVariable: (variable: string) => void;
};

const CustomEditor = forwardRef<CustomEditorRef, CustomEditorProps>(
  ({ value, variablesOptions, onValidate }, ref) => {
    const editorRef = useRef<any>(null);
    const monacoRef = useRef<any>(null);
    const completionProviderRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
      getValue: () => editorRef.current?.getValue() || '',
      insertVariable: (variable: string) => {
        const editor = editorRef.current;
        const monaco = monacoRef.current;
        if (!editor || !monaco) return;

        const position = editor.getPosition();
        if (!position) return;

        editor.executeEdits(null, [
          {
            range: new monaco.Range(
              position.lineNumber,
              position.column,
              position.lineNumber,
              position.column
            ),
            text: `\${${variable}}`,
            forceMoveMarkers: true,
          },
        ]);

        editor.focus();
        editor.setPosition({
          lineNumber: position.lineNumber,
          column: Number(position.column) + String(variable).length + 3,
        });
      },
    }));

    const handleEditorWillMount = (monaco: any) => {
      const registerLanguageNoExists = !monaco.languages
        .getLanguages()
        .some((lang: any) => lang.id === defaultLanguage);
      console.log('registerLanguageNoExists: ', registerLanguageNoExists);
      if (!registerLanguageNoExists) return;

      monaco.languages.register({ id: defaultLanguage });

      monaco.languages.setMonarchTokensProvider(defaultLanguage, {
        brackets: [
          ['{', '}', 'delimiter.curly'],
          ['[', ']', 'delimiter.square'],
        ],
        tokenizer: {
          root: [
            [/"[^"]+"\s*:/, 'string.key.json'],
            [/\$\{[^}]+\}/, 'custom-variable'],
            [/"([^"\\]|\\.)*"/, 'string.value.json'],
            [/[{}[\]]/, '@brackets'],
            [/"([^"\\]|\\.)*$/, 'string.invalid'],
            [/"/, { token: 'string.quote', bracket: '@open', next: '@string' }],
            [/\s+/, 'white'],
          ],
          string: [
            [/\$\{[^}]+\}/, 'custom-variable'],
            [/[^\\"]+/, 'string'],
            [/\\./, 'string.escape.invalid'],
            [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
          ],
        },
      });

      if (completionProviderRef.current) {
        completionProviderRef.current.dispose();
      }

      completionProviderRef.current = monaco.languages.registerCompletionItemProvider(
        defaultLanguage,
        {
          provideCompletionItems: (model: any, position: any) => {
            const suggestions = variablesOptions.map((option) => ({
              label: option.label,
              kind: monaco.languages.CompletionItemKind.Variable,
              insertText: `\${${option.id}#${option.value}}`,
              documentation: `Value: ${option.value}`,
              range: new monaco.Range(
                position.lineNumber,
                position.column,
                position.lineNumber,
                position.column
              ),
            }));

            return { suggestions };
          },
        }
      );

      // monaco.languages.registerHoverProvider(defaultLanguage, {
      //   provideHover: function (model: any, position: any) {
      //     const lineText = model.getLineContent(position.lineNumber);
      //     const regex = /\$\{([^}]+)\}/g;
      //     let match;

      //     while ((match = regex.exec(lineText))) {
      //       const start = match.index;
      //       const end = start + match[0].length;

      //       const column = position.column - 1;
      //       if (column >= start && column <= end) {
      //         const variableName = match[1];
      //         const validKeys = new Set(variablesOptions.map((o) => `${o.id}#${o.value}`));
      //         if (validKeys.has(variableName)) {
      //           const variableValue = variablesOptions.find(
      //             (o) => `${o.id}#${o.value}` === variableName
      //           );
      //           return {
      //             range: new monaco.Range(
      //               position.lineNumber,
      //               start + 1,
      //               position.lineNumber,
      //               end + 1
      //             ),
      //             contents: [{ value: `**Value:** ${String(variableValue?.value)}` }],
      //           };
      //         }
      //       }
      //     }

      //     return null;
      //   },
      // });

      monaco.editor.defineTheme('my-theme', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          { token: 'custom-variable', foreground: '4FC3F7', fontStyle: 'italic' },
          { token: 'delimiter.curly', foreground: '#ffffff' },
          { token: 'delimiter.square', foreground: '#ffffff' },
          { token: 'string.key.json', foreground: '#FC3293' },
          { token: 'string.value.json', foreground: '#8543D3' },
        ],
        colors: {
          'editor.background': '#002646',
        },
      });
    };

    const extractVariables = (text: string): VariableEditor[] => {
      const lines = text.split('\n');
      const result: VariableEditor[] = [];

      lines.forEach((line, lineIndex) => {
        const regex = /\$\{([^}]+)\}/g;
        let match;
        while ((match = regex.exec(line))) {
          const variable = match[1];
          const [id, key] = variable.split('#');
          result.push({
            id: id,
            key: key,
            start: match.index,
            end: match.index + match[0].length,
            line: lineIndex + 1,
          });
        }
      });

      return result;
    };

    const markInvalidVariables = (editor: any, monaco: any, options: DropdownItemProps[]) => {
      const model = editor.getModel();
      const value = model.getValue();

      const variables = extractVariables(value);
      const validKeys = new Set(options.map((o) => `${o.id}#${o.value}`));

      const markers = variables
        .filter((v) => !validKeys.has(`${v.id}#${v.key}`))
        .map((v) => ({
          severity: monaco.MarkerSeverity.Error,
          message: `This variable does not exist: ${v.id}#${v.key}`,
          startLineNumber: v.line,
          endLineNumber: v.line,
          startColumn: v.start + 1,
          endColumn: v.end + 1,
        }));

      monaco.editor.setModelMarkers(model, 'custom-validation', markers);
    };

    const validateJsonSyntax = (editor: any, monaco: any) => {
      const model = editor.getModel();
      const value = model.getValue();
      const markers: any[] = [];

      const lines = value.split('\n');
      const sanitizedLines: string[] = [];

      lines.forEach((line: string, i: number) => {
        let sanitizedLine = '';
        let cursor = 0;

        // Detect single quotes and mark them as errors
        const singleQuoteIndex = line.indexOf("'");
        if (singleQuoteIndex !== -1) {
          markers.push({
            severity: monaco.MarkerSeverity.Error,
            message: `Invalid single quote in line ${i + 1}. Use double quotes for JSON strings.`,
            startLineNumber: i + 1,
            endLineNumber: i + 1,
            startColumn: singleQuoteIndex + 1,
            endColumn: singleQuoteIndex + 2,
          });
        }

        const regex = /\$\{[^}]+\}/g;
        let match;

        while ((match = regex.exec(line)) !== null) {
          const start = match.index;
          const end = start + match[0].length;

          // add pre-text
          sanitizedLine += line.slice(cursor, start);

          // Determine if it is inside double quotes
          const quoteCount = (line.slice(0, start).match(/"/g) || []).length;
          const isInsideQuotes = quoteCount % 2 === 1;

          sanitizedLine += isInsideQuotes ? '__VAR__' : '"__VAR__"';

          cursor = end;
        }

        sanitizedLine += line.slice(cursor);
        sanitizedLines.push(sanitizedLine);
      });

      const sanitizedJson = sanitizedLines.join('\n');

      try {
        JSON.parse(sanitizedJson);
      } catch (err: any) {
        const message = err.message;

        // Try to extract the line number from the error message
        let errorLine = 1;
        const lineMatch = message.match(/at position (\d+)/);
        if (lineMatch && lineMatch[1]) {
          const pos = parseInt(lineMatch[1], 10);
          // Find the line number based on the character position
          let charCount = 0;
          for (let i = 0; i < sanitizedLines.length; i++) {
            charCount += sanitizedLines[i].length + 1; // +1 for newline
            if (pos < charCount) {
              errorLine = i + 1;
              break;
            }
          }
        }

        markers.push({
          severity: monaco.MarkerSeverity.Error,
          message: `invalid JSON: ${message}`,
          startLineNumber: errorLine,
          endLineNumber: errorLine,
          startColumn: 1,
          endColumn: 1,
        });
      }

      monaco.editor.setModelMarkers(model, 'json-validation', markers);
    };

    const highlightVariables = (editor: any, monaco: any) => {
      const model = editor.getModel();
      const value = model.getValue();

      const decorations: any = [];
      const regex = /\$\{[^}]+\}/g;
      const lines = value.split('\n');

      lines.forEach((line: string, i: number) => {
        let match;
        while ((match = regex.exec(line))) {
          const startColumn = match.index + 1;
          const endColumn = match.index + match[0].length + 1;

          decorations.push({
            range: new monaco.Range(i + 1, startColumn, i + 1, endColumn),
            options: {
              inlineClassName: 'variable-highlight',
              stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
            },
          });
        }
      });

      editor.deltaDecorations([], decorations);
    };

    const handleValidateErrors = () => {
      if (typeof onValidate === 'function') {
        const editor = editorRef.current;
        const monaco = monacoRef.current;
        if (!editor || !monaco) return;
        const model = editor.getModel();
        const markers = monaco.editor.getModelMarkers({ resource: model.uri });
        const hasErrors = markers.some(
          (marker: any) => marker.severity === monaco.MarkerSeverity.Error
        );
        onValidate(hasErrors);
      }
    };

    return (
      <div className="w-full rounded bg-emblue-tiles py-4">
        <Editor
          beforeMount={handleEditorWillMount}
          defaultLanguage={defaultLanguage}
          defaultValue={value}
          height={'280px'}
          options={{
            readOnly: false,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            lineNumbers: 'off',
            contextmenu: false,
          }}
          theme="my-theme"
          value={value}
          width="100%"
          onMount={(editor, monaco) => {
            editorRef.current = editor;
            monacoRef.current = monaco;

            markInvalidVariables(editor, monaco, variablesOptions);
            highlightVariables(editor, monaco);
            validateJsonSyntax(editor, monaco);
            handleValidateErrors();

            editor.onDidChangeModelContent(() => {
              markInvalidVariables(editor, monaco, variablesOptions);
              highlightVariables(editor, monaco);
              validateJsonSyntax(editor, monaco);
              handleValidateErrors();
            });
          }}
        />
      </div>
    );
  }
);

export default memo(CustomEditor);
